// components/dashboard/AddUnit/ListOfUnits/ListOfUnits.tsx - Оновлено під нову архітектуру
import React, { useImperativeHandle, forwardRef } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIButton from '@/components/shared/UIButton';
import UnitsListTable from '../UnitsListTable';
import UIPaginator from '@/components/shared/UIPaginator';
import { usePropertyUnits } from '@/hooks/usePropertyUnits';
import { UnitWithLeaseInfo } from '@/types';
import './ListOfUnits.scss';

export interface ListOfUnitsProps {
  title?: string;
  propertyId?: number | string;
  onUnitAction?: (unitId: string, action: string, unitNumber?: string) => void;
  onAddUnit?: () => void;
  itemsPerPage?: number;
}

interface ListOfUnitsRefType {
  refetch: () => void;
}

const baseClass = 'ListOfUnits';

const ListOfUnitsComponent: React.ForwardRefRenderFunction<ListOfUnitsRefType, ListOfUnitsProps> = ({
  title = 'List of units',
  propertyId,
  onUnitAction = () => {},
  onAddUnit = () => {},
  itemsPerPage = 7,
}, ref) => {
  // console.log('ListOfUnits render - propertyId:', propertyId);

  // Використовуємо хук тільки якщо є propertyId
  const { units, loading, error, meta, changePage, refetch } = usePropertyUnits({
    propertyId: propertyId ? propertyId : undefined,
    autoFetch: !!propertyId,
  });

  // "Оголошуємо" функцію refetch для батьківського компонента
  useImperativeHandle(ref, () => ({
    refetch,
  }));

  // Якщо немає propertyId - не рендеримо нічого або показуємо повідомлення
  if (!propertyId) {
    return (
      <UISectionWrapper className={baseClass} align="stretch">
        <UISectionHeader>
          <UISectionTitle title={title} showDivider={true} />
        </UISectionHeader>
        <UISectionBody>
          <div className={`${baseClass}__table-placeholder`}>
            Property ID is required to load units.
          </div>
        </UISectionBody>
      </UISectionWrapper>
    );
  }

  const handlePageChange = (page: number) => {
    changePage(page, {
      limit: itemsPerPage,
    });
  };

  const headerAction = (
    <UIButton
      variant="primary"
      size="medium"
      onClick={onAddUnit}
      disabled={loading}
    >
      + Add unit
    </UIButton>
  );

  return (
    <UISectionWrapper className={baseClass} align="stretch">
      <UISectionHeader>
        <UISectionTitle
          title={`${title} (${meta.total})`}
          description=""
          action={headerAction}
          showDivider={true}
        />
      </UISectionHeader>

      <UISectionBody>
        <div className={`${baseClass}__content`}>
          {error ? (
            <div className={`${baseClass}__table-placeholder`}>
              Error loading units: {error}
            </div>
          ) : loading ? (
            <div className={`${baseClass}__table-placeholder`}>
              Loading units...
            </div>
          ) : units.length === 0 ? (
            <div className={`${baseClass}__table-placeholder`}>
              No units available.
            </div>
          ) : (
            <>
              <UnitsListTable data={units} onUnitAction={onUnitAction} />

              {meta.totalPages > 1 && (
                <UIPaginator
                  currentPage={meta.page || 1}
                  totalPages={meta.totalPages}
                  onPageChange={handlePageChange}
                  className={`${baseClass}__paginator`}
                />
              )}
            </>
          )}
        </div>
      </UISectionBody>
    </UISectionWrapper>
  );
};

export const ListOfUnits = forwardRef(ListOfUnitsComponent);

// Експортуємо тип для зворотної сумісності
export type { UnitWithLeaseInfo as UnitListData };
export type { ListOfUnitsRefType as ListOfUnitsRef };
